import { Button } from "@/components/ui/button";
import { cn } from "@/components/utils/utils";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { Plus } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { UserRole } from "@/lib/auth/models/auth.model";

interface TravelRequestButtonProps {
  travelRequest: DashboardTravelRequest;
  userRole: UserRole;
  onClick: (requestId: string) => void;
  onCreateItinerary?: (requestId: string) => void;
  variant?: "card" | "table";
  className?: string;
}

type ButtonState = 
  | "admin-assign"
  | "admin-view"
  | "completed-view"
  | "expired-view"
  | "designer-create"
  | "designer-accept"
  | "traveler-view";

export const TravelRequestButton = ({
  travelRequest,
  userRole,
  onClick,
  onCreateItinerary,
  variant = "card",
  className
}: TravelRequestButtonProps) => {
  const { t } = useI18n();
  
  // Determina lo stato del pulsante in base a ruolo e status
  const getButtonState = (): ButtonState => {
    // Priorità massima: controlla prima il ruolo dell'utente
    if (userRole === "admin") {
      return travelRequest.status === "pending" && !travelRequest.isExpired
        ? "admin-assign"
        : "admin-view";
    }
    if (userRole === "traveler") return "traveler-view";
    
    // Logic per travel_designer
    if (travelRequest.status === "completed") return "completed-view";
    
    // Check se la richiesta è scaduta (pending da più di 48 ore)
    if (travelRequest.isExpired) return "expired-view";
    
    const isAssigned = !!travelRequest.assignedAdvisor?.id && travelRequest.status === "assigned";
    return isAssigned ? "designer-create" : "designer-accept";
  };

  const buttonState = getButtonState();

  // Handler del click basato sullo stato
  const handleClick = () => {
    if (buttonState === "designer-create" && onCreateItinerary) {
      onCreateItinerary(travelRequest.id);
    } else {
      onClick(travelRequest.id);
    }
  };

  // Configurazione del pulsante basata sullo stato
  const getButtonConfig = () => {
    switch (buttonState) {
      case "admin-assign":
        return {
          text: t('travelRequest.assign'),
          variant: "default" as const,
          showIcon: false
        };

      case "admin-view":
        return {
          text: t('travelRequest.viewRequest'),
          variant: "secondary" as const,
          showIcon: false
        };
      
      case "completed-view":
        return {
          text: `${t('common.view')} ${t('travelRequest.requestDetails')}`,
          variant: "secondary" as const,
          showIcon: false
        };
      
      case "expired-view":
        return {
          text: t('travelRequest.viewRequest'),
          variant: "outline" as const,
          showIcon: false
        };
      
      case "designer-create":
        return {
          text: t('itinerary.createItinerary'),
          variant: "default" as const,
          showIcon: true
        };
      
      case "designer-accept":
        return {
          text: t('travelRequest.acceptOrDecline'),
          variant: travelRequest.isPending ? "secondary" as const : "outline" as const,
          showIcon: false
        };
      
      case "traveler-view":
        return {
          text: t('travelRequest.viewRequest'),
          variant: "secondary" as const,
          showIcon: false
        };
    }
  };

  const config = getButtonConfig();

  // Rendering basato sulla variante (card o table)
  if (variant === "card") {
    if (buttonState === "designer-create") {
      return (
        <div className="flex flex-col gap-2">
          <Button
            variant="default"
            className={cn("w-full h-12 rounded-xl", className)}
            onClick={() => onCreateItinerary && onCreateItinerary(travelRequest.id)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('itinerary.createItinerary')}
          </Button>
          <Button
            variant="secondary"
            className={cn("w-full h-10 rounded-xl", className)}
            onClick={() => onClick(travelRequest.id)}
          >
            {t('travelRequest.viewRequest')}
          </Button>
        </div>
      );
    }

    return (
      <Button
        variant={config.variant}
        className={cn("w-full h-12 rounded-xl", className)}
        onClick={handleClick}
      >
        {config.showIcon && <Plus className="h-4 w-4 mr-2" />}
        {config.text}
      </Button>
    );
  } else {
    return (
      <Button
        variant={config.variant}
        size="sm"
        onClick={handleClick}
        className={cn("text-sm", className)}
      >
        {config.showIcon && <Plus className="h-3 w-3 mr-1" />}
        {config.text}
      </Button>
    );
  }

  // Table variant
};